import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { Icon } from './AppShell';
import { downloadFile } from '../api/reports';

// Siyahı və səhifə şablonu (KT-99): bölmə rəngində başlıq (+ Yeni …, Yüklə ▾), açılan «Axtarış» paneli (süzgəc URL-də),
// cədvəl (✎ redaktə, boş/yüklənmə/xəta halı, cəmi sətri), səhifələmə sağ altda. Bütün siyahılar bunu işlədir.

/** Bölmə rəngində səhifə başlığı. Yol (Ana səhifə / Bölmə / Səhifə) qabıqdadır. */
export function PageHeader({ title, sub, actions, tabs }: { title: string; sub?: ReactNode; actions?: ReactNode; tabs?: ReactNode }) {
  return (
    <header className="page-head">
      <div className="page-head-title">
        <h1>{title}</h1>
        {sub && <span className="page-head-sub">{sub}</span>}
      </div>
      {actions && <div className="page-head-actions">{actions}</div>}
      {tabs && <div className="page-head-tabs">{tabs}</div>}
    </header>
  );
}

/** «+ Yeni …» — başlıqda əsas düymə (ağ fonda bölmə rəngi). */
export function NewButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="head-btn head-btn-main" onClick={onClick}>
      <Icon d="M12 5v14 M5 12h14" size={16} />
      {label}
    </button>
  );
}

export function HeadButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="head-btn" onClick={onClick}>
      {label}
    </button>
  );
}

/** «Yüklə ▾»: Excel (PDF olanda PDF). */
export function DownloadMenu({ items }: { items: { label: string; url: string; name: string }[] }) {
  const [error, setError] = useState<string | null>(null);
  if (items.length === 0) return null;
  return (
    <details className="download-menu">
      <summary className="btn-small">Yüklə ▾</summary>
      <div className="download-list" role="menu">
        {items.map((i) => (
          <button
            key={i.url}
            type="button"
            role="menuitem"
            onClick={() => {
              setError(null);
              downloadFile(i.url, i.name).catch(() => setError('Fayl yüklənmədi. Yenidən cəhd edin.'));
            }}
          >
            {i.label}
          </button>
        ))}
        {error && (
          <p className="bad small" role="alert">
            {error}
          </p>
        )}
      </div>
    </details>
  );
}

/**
 * Süzgəc kartı (KT-108, maket 26): sahələr bir sırada, «Axtar» və «Təmizlə»; susmaya görə açıq (telefonda bağlana bilər).
 * defaultOpen — köhnə çağırışlar üçün (KT-99-da bağlı idi); indi yalnız açıq vəziyyəti məcbur edir.
 */
export function SearchPanel({ children, onSearch, onClear, defaultOpen = true }: { children: ReactNode; onSearch: () => void; onClear: () => void; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen || typeof window === 'undefined' || window.innerWidth > 640);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSearch();
  };
  return (
    <section className="search-panel">
      <button type="button" className="search-panel-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <Icon d={open ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} size={14} />
        Axtarış
      </button>
      {open && (
        <form className="search-panel-body" onSubmit={submit} role="search">
          {children}
          <div className="search-panel-actions">
            <button type="submit" className="btn-sec">
              Axtar
            </button>
            <button type="button" className="btn-small" onClick={onClear}>
              Təmizlə
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export interface Column<T> {
  key: string;
  label: string;
  align?: 'right';
  mono?: boolean;
  /** Telefonda gizlənən köməkçi sütun. */
  minor?: boolean;
  /** Sıralama açarı (KT-108): başlığa basanda artan/azalan; «-» ilə azalan */
  sortKey?: string;
  render: (row: T) => ReactNode;
}

interface TableProps<T> {
  label: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onEdit?: (row: T) => void;
  editLabel?: string;
  loading: boolean;
  error: string | null;
  empty: ReactNode;
  /** Səhifə cəmi sətri (tünd): sütun açarı → dəyər. */
  footer?: Record<string, ReactNode>;
  /** Cari sıralama (məs. 'name', '-price') və dəyişmə */
  sort?: string;
  onSort?: (sort: string) => void;
}

export function DataTable<T>({ label, columns, rows, rowKey, onEdit, editLabel = 'Redaktə et', loading, error, empty, footer, sort, onSort }: TableProps<T>) {
  if (error) {
    return (
      <p className="bad" role="alert">
        {error}
      </p>
    );
  }
  if (loading && rows.length === 0) return <p className="muted">Yüklənir…</p>;
  if (rows.length === 0) return <div className="table-empty">{empty}</div>;
  const cls = (c: Column<T>) => [c.align === 'right' ? 'num' : '', c.mono ? 'mono' : '', c.minor ? 'minor' : ''].filter(Boolean).join(' ') || undefined;
  return (
    <div className="table-wrap" aria-busy={loading}>
      <table className="data-table" aria-label={label}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={cls(c)} aria-sort={c.sortKey && sort?.replace('-', '') === c.sortKey ? (sort.startsWith('-') ? 'descending' : 'ascending') : undefined}>
                {c.sortKey && onSort ? (
                  <button type="button" className="th-sort" onClick={() => onSort(sort === c.sortKey ? `-${c.sortKey}` : c.sortKey!)}>
                    {c.label}
                    <span aria-hidden="true">{sort === c.sortKey ? ' ↑' : sort === `-${c.sortKey}` ? ' ↓' : ' ↕'}</span>
                  </button>
                ) : (
                  c.label
                )}
              </th>
            ))}
            {onEdit && (
              <th scope="col" className="edit-col">
                <span className="sr-only">{editLabel}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)}>
              {columns.map((c) => (
                <td key={c.key} className={cls(c)}>
                  {c.render(r)}
                </td>
              ))}
              {onEdit && (
                <td className="edit-col">
                  <button type="button" className="edit-btn" aria-label={editLabel} onClick={() => onEdit(r)}>
                    <Icon d="M4 20h4L19 9l-4-4L4 16z M14 6l4 4" size={18} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr>
              {columns.map((c) => (
                <td key={c.key} className={cls(c)}>
                  {footer[c.key] ?? ''}
                </td>
              ))}
              {onEdit && <td />}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/** Səhifələmə: «1–25 / 412 məhsul», sağda ‹ 1 2 3 ›. */
export function Pager({ page, limit, total, unit, onPage }: { page: number; limit: number; total: number; unit: string; onPage: (p: number) => void }) {
  if (total === 0) return null;
  const pages = Math.max(1, Math.ceil(total / limit));
  const from = (page - 1) * limit + 1;
  const to = Math.min(total, page * limit);
  const nums = [...new Set([1, page - 1, page, page + 1, pages])].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  return (
    <nav className="pager2" aria-label="Səhifələr">
      <span className="muted">
        {from}–{to} / {total} {unit} göstərilir
      </span>
      {pages > 1 && (
        <div className="pager2-btns">
          <button type="button" aria-label="Əvvəlki səhifə" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            ‹
          </button>
          {nums.map((n) => (
            <button key={n} type="button" aria-current={n === page ? 'page' : undefined} onClick={() => onPage(n)}>
              {n}
            </button>
          ))}
          <button type="button" aria-label="Növbəti səhifə" disabled={page >= pages} onClick={() => onPage(page + 1)}>
            ›
          </button>
        </div>
      )}
    </nav>
  );
}

/** Tez tarix düymələri (sənəd siyahıları): bu gün, bu ay, son 30 gün. */
export function QuickDates({ value, onChange }: { value: string; onChange: (v: 'day' | 'month' | '30d') => void }) {
  const opts: ['day' | 'month' | '30d', string][] = [
    ['day', 'Bu gün'],
    ['month', 'Bu ay'],
    ['30d', 'Son 30 gün'],
  ];
  return (
    <div className="quick-dates" role="group" aria-label="Dövr">
      {opts.map(([k, l]) => (
        <button key={k} type="button" className={value === k ? 'm-chip m-chip-on' : 'm-chip'} aria-pressed={value === k} onClick={() => onChange(k)}>
          {l}
        </button>
      ))}
    </div>
  );
}

/**
 * Süzgəc URL-də (?q=…&page=…): səhifə yenilənəndə və linklə paylaşanda qalır. Dəyər yalnız sətirdir; boş dəyər silinir.
 */
export function useUrlFilters<K extends string>(keys: readonly K[]): [Record<K, string>, (patch: Partial<Record<K, string>>) => void] {
  const read = () => {
    const sp = new URLSearchParams(window.location.search);
    return Object.fromEntries(keys.map((k) => [k, sp.get(k) ?? ''])) as Record<K, string>;
  };
  const [values, setValues] = useState<Record<K, string>>(read);
  const update = useCallback(
    (patch: Partial<Record<K, string>>) => {
      setValues((cur) => {
        const next = { ...cur, ...patch };
        const sp = new URLSearchParams(window.location.search);
        for (const k of keys) {
          if (next[k]) sp.set(k, next[k]);
          else sp.delete(k);
        }
        const qs = sp.toString();
        window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
        return next;
      });
    },
    // keys sabit massivdir (modul səviyyəsində)
    [],
  );
  return [values, update];
}
